/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../routing/state-utils';
import _ from 'lodash';
import {OBJECT_TYPE} from "../common/constants/object-type";
import {PERMISSIONS} from "../../components/auth/auth-constants";
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {LibraryCreativeListContextComponent} from '../library-creative/library-creative-list-context.component';
import {CreativeBulkUploadPageComponent} from './creative-bulk-upload-page.component';
import {CreativeContextActionsComponent} from './creative-context-actions.component';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {resolveDefaultCreativeName} from './creative-helper.service';
import {CreativeApiService, CreativeModel} from './creative-api.service';
import {CreativeEditPageComponent} from './creative-edit-page.component';
import {CreativeListPageComponent} from './creative-list-page.component';
import {
  creativeSetBreadcrumbData,
  CreativeSetInfoApiService
} from '../creative-set/creative-set-info-api.service';

export function creativeStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
const creativeApi = adnRouterHelper.services.service(CreativeApiService);
const permissions = adnRouterHelper.services.localUserPermissions();
const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
const dataViewApi = adnRouterHelper.services.dataViewApi();
const creativeSetInfoApi = adnRouterHelper.services.service(CreativeSetInfoApiService);
const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
  && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey)
  && !permissions.hasAny(PERMISSIONS.creativeAgency.apiKey);
const createCreativePage = (model: CreativeModel, isNew: boolean, lineItemInfo: any) => {
  const lineItem = model.lineItem?.id
    ? model.lineItem
    : lineItemInfo.fromLineItem && lineItemInfo.id
      ? {id: lineItemInfo.id, name: lineItemInfo.name}
      : undefined;
  const creativeSet = model.creativeSet?.id
    ? model.creativeSet
    : lineItemInfo.fromCreativeSet && lineItemInfo.id
      ? {id: lineItemInfo.id, name: lineItemInfo.name}
      : undefined;
  let crumbTypes: string[] = [];
  if (lineItem?.id) {
    crumbTypes = permissions.isSelfService() || permissions.isAxPublisherOnly()
      ? ['LineItem']
      : ['Advertiser', 'Order', 'LineItem'];
  } else if (creativeSet?.id) {
    crumbTypes = ['Advertiser', 'Folio', 'CreativeSet'];
  }

  let page: SignalAdminResourcePageState<CreativeModel>;
  const crumbParams = crumbTypes.map((type) => ({
    type,
    resolve: (data: CreativeModel) => {
      const source = page.relatedData().creativeBreadcrumbs || data;
      return source?.[OBJECT_TYPE[type].apiParam];
    }
  }));
  page = new SignalAdminResourcePageState(
    model, isNew, readOnly(), creativeApi, 'Creative', 'name', undefined, undefined,
    {objectTypeKey: 'Creative', crumbParams}
  );

  if (lineItem?.id) {
    page.updateRelatedData('creativeBreadcrumbs', {...model, lineItem});
    dataViewApi.getLineItemViewFromCache(lineItem.id).subscribe({
      next: (dataView) => page.updateRelatedData('creativeBreadcrumbs', dataView),
      error: () => undefined
    });
  } else if (creativeSet?.id) {
    const advertiser = lineItemInfo.advertiserId
      ? {id: lineItemInfo.advertiserId, name: lineItemInfo.advertiserName}
      : undefined;
    page.updateRelatedData('creativeBreadcrumbs', creativeSetBreadcrumbData(creativeSet, undefined, {advertiser}));
    creativeSetInfoApi.get(creativeSet.id).subscribe({
      next: (info) => page.updateRelatedData(
        'creativeBreadcrumbs',
        creativeSetBreadcrumbData(creativeSet, info, {advertiser})
      ),
      error: () => undefined
    });
  }
  return page;
};
let rh = adnRouterHelper.createRouterHelper('creatives.creative', CreativeApiService, 'app.adv', 'app');
    
        let paramsExtras = {modelId: '', modelName: '', fromLineItem: false, fromCreativeSet: false};
        states.add(rh.listPath(), rh.list({
          views: _.merge({}, {
            'content@app': {component: CreativeListPageComponent, bindings: {readOnly: 'listReadOnly'}},
            'context@app': {
              component: LibraryCreativeListContextComponent,
              bindings: {readOnly: 'listReadOnly'}
            }
          }),
          resolve: {
            readOnly,
            sectionParam: function() {
              return 'creatives';
            }
          }
        }))
          .add(rh.newPath('bulkupload'), rh.item({
            url: '/bulk-upload',
            params: {lineItemId: false, lineItemName: false, creativeSetId: false, creativeSetName: false},
            resolve: {
              model: function() {
                return null;
              },
              creativeType: function() {
                return 'CREATIVE';
              }
            },
            views: {
              'content@app': {
                component: CreativeBulkUploadPageComponent,
                bindings: {creativeType: 'creativeType'}
              },
              'title@app': stateTitleView()
            },
            data: {
              title: 'Creative Bulk Upload'
            }
          }))
          .add(rh.newItemPath(), rh.newItem(paramsExtras))
          .add(rh.itemPath(), rh.item({
            params: paramsExtras,
            resolve: {
              creativeType: function() {
                return OBJECT_TYPE.Creative;
              },
              lineItemInfo: function($stateParams) {
                return {
                  id: $stateParams.modelId,
                  name: $stateParams.modelName,
                  fromLineItem: $stateParams.fromLineItem,
                  fromCreativeSet: $stateParams.fromCreativeSet,
                  advertiserId: $stateParams.advertiserId,
                  advertiserName: $stateParams.advertiserName
                };
              },
              creativePage: function($stateParams, lineItemInfo) {
                const LocalNetworkProfile = adnRouterHelper.services.localNetworkProfile();

                if ($stateParams.isNew) {
                  let networkDefaults = LocalNetworkProfile.getDefaults();
                  const model: CreativeModel = {
                    name: $stateParams.modelName ? resolveDefaultCreativeName(networkDefaults.defaultCreativeName, $stateParams.modelName) : "",
                    lineItem: {id: $stateParams.fromLineItem ? $stateParams.modelId : ''},
                    creativeSet: {id: $stateParams.fromCreativeSet ? $stateParams.modelId : ''},
                    id: $stateParams.id,
                    objectState: 'ACTIVE',
                    validationWarnings: []
                  };
                  return createCreativePage(model, true, lineItemInfo);
                }
                return firstValueFrom(creativeApi.getHydrated($stateParams.id).pipe(
                  tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
                )).then((model) => createCreativePage(model, false, lineItemInfo));
              },
              model: (creativePage: SignalAdminResourcePageState<CreativeModel>) => creativePage.model()
            },
            views: {
              'content@app': {
                component: CreativeEditPageComponent,
                bindings: {state: 'creativePage', creativeType: 'creativeType', lineItemInfo: 'lineItemInfo'}
              },
              'context@app': {
                component: CreativeContextActionsComponent,
                bindings: {state: 'creativePage'}
              },
              'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'creativePage'}}
            }
          }));
  return states.build();
}
